import gsap from "gsap"
import Flip from "gsap/Flip"
import ScrollTrigger from "gsap/ScrollTrigger"
import ScrollToPlugin from "gsap/ScrollToPlugin"
import imagesLoaded from "imagesloaded"
import Splide from '@splidejs/splide';
import Loadeer from "loadeer";

gsap.registerPlugin(Flip, ScrollTrigger, ScrollToPlugin)

history.scrollRestoration = 'manual';

// --------------------------------------------------
// ✅ General
// --------------------------------------------------

const debounce = (callback, wait) => {
  let timeoutId = null;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback.apply(null, args);
    }, wait);
  };
}

// --------------------------------------------------
// 💤 Lazy Loading
// --------------------------------------------------

let loadeerOptions = {
  onLoaded: (element) => {
    element.addEventListener('load', () => {
      gsap.to(element, {
        autoAlpha: 1,
        duration: .5,
        ease: 'none',
      })
    })
  }
}
const loadeer = new Loadeer(document.querySelectorAll('[data-lazyload]'), loadeerOptions);
loadeer.observe();

// --------------------------------------------------
// 📏 Marek's Grid
// --------------------------------------------------

let grid = document.querySelector('.grid');

let toggleGrid = gsap.to(grid, {
  duration: .5,
  ease: 'power2.inOut',
  scaleY: 1,
  paused: true
}).reverse();

document.addEventListener('keydown', e => {
  if (e.key === 'g' && e.ctrlKey) {
    toggleGrid.reversed() ? toggleGrid.play() : toggleGrid.reverse();
    document.documentElement.classList.toggle('debug');
  }
})

// --------------------------------------------------
// 📹 Videos
// --------------------------------------------------

let videoHero = document.querySelector('.video-hero')
let videos = gsap.utils.toArray(".video")
let visibleVideos = gsap.utils.toArray(".video:not(.video--hidden)")
let currentVideo = document.querySelector(".video:last-child")
let nextVideo
let playedVideos = [];
let errors = [];
playedVideos.push(currentVideo)

function loadNextVideo(immediate = false) {

  visibleVideos = gsap.utils.toArray(".video:not(.video--hidden)")

  if(visibleVideos.length <= playedVideos.length) {
    playedVideos = [];
  }

  let videosWithoutPlayedVideos = visibleVideos.filter(video => !playedVideos.includes(video))

  nextVideo = videosWithoutPlayedVideos[Math.floor(Math.random() * videosWithoutPlayedVideos.length)]
  nextVideo.querySelector('video').setAttribute("preload", "auto")

  if (nextVideo.querySelector('video').readyState < 4 && nextVideo.querySelector('video').paused) {
    nextVideo.querySelector('video').load()
  }

  if(immediate) {
    playNextVideo()
  }
  else {
    gsap.delayedCall(currentVideo.dataset.interval, playNextVideo)
  }

}

function playNextVideo() {

  if(currentVideo.querySelector('video').readyState < 4) {
    errors.push(currentVideo);
  }

  if(errors.length > 1) {
    videoHero.innerHTML = '<div class="video-hero__error">Your internet connection is too slow.</div>';
  }

  else {
  
    nextVideo.querySelector('video').currentTime = 0
  
    let playPromise = nextVideo.querySelector('video').play()
  
    if (playPromise !== undefined) {
      playPromise.then(_ => {
        nextVideo.querySelector('video').dataset.playedAsPromised = true
      })
      .catch(error => {
      });
    }
    
    if(currentVideo != nextVideo) {
  
      if(currentVideo.querySelector('video').dataset.playedAsPromised === 'true') {
        currentVideo.querySelector('video').pause()
      }
  
      currentVideo.currentTime = 0
      currentVideo = nextVideo
      playedVideos.push(currentVideo)
      videoHero.querySelector('.video-hero__inner').appendChild(nextVideo)
  
    }
  
    loadNextVideo()

  }

}

if(videoHero && currentVideo) {

  document.documentElement.classList.add('loading')

  gsap.to(videoHero, {
    autoAlpha: 0,
    ease: 'none',
    scrollTrigger: {
      trigger: videoHero,
      start: 'top top',
      end: 'bottom 25%',
      invalidateOnRefresh: false,
      scrub: true,
      onLeave: () => {
        videoHero.classList.add('video-hero--not-in-viewport')
        gsap.killTweensOf(playNextVideo);
        if(!currentVideo.querySelector('video').paused) {
          currentVideo.querySelector('video').pause()
        }
      },
      onEnterBack: () => {
        videoHero.classList.remove('video-hero--not-in-viewport')
        gsap.killTweensOf(playNextVideo);
        playedVideos = [];
        loadNextVideo(true)
      }
    }
  })

  let firstVideo = currentVideo.querySelector('video');
  let firstVideoPlayed = false;

  function showFirstVideo() {
    if(!firstVideoPlayed) {
      loadNextVideo()
      firstVideo.currentTime = 0;
      firstVideo.play()
      gsap.to(videoHero.querySelector('.video-hero__inner'), {
        autoAlpha: 1,
        ease: 'none',
        duration: .25
      })
      document.documentElement.classList.remove('loading')
      firstVideoPlayed = true;
    }
  }

  if (firstVideo.readyState > 2) {
    showFirstVideo();
  }
  else {
    firstVideo.addEventListener('canplay', function () {
      showFirstVideo();
    }, false);
  }

}

// --------------------------------------------------
// 🔘 Toggles
// --------------------------------------------------

let toggles = document.querySelectorAll(".toggle")
toggles.forEach(toggle => {
  toggle.addEventListener('click', (e) => {

    if(toggle.classList.contains('filter__toggle') && toggle.classList.contains('toggle--active')) {
      if(document.querySelectorAll('.filter__toggle.toggle--active').length === 1) {
        return false
      }
    }

    if(toggle.matches('.langnav .toggle')) {
      return false;
    }

    let state = Flip.getState([toggle.querySelectorAll('span')], {
      simple: true
    })

    toggle.classList.toggle('toggle--active')

    Flip.from(state, {
      duration: 0.25,
      force3D: true,
      ease: "power1.inOut",
      onComplete: () => {
        if(toggle.classList.contains('filter__toggle')) {
          filter()
          gsap.killTweensOf(playNextVideo);
          playedVideos = [];
          errors = [];
          loadNextVideo(true)
        }
        // else if(toggle.matches('.langnav .toggle')) {
        //   window.location.href = toggle.href
        // }
      }
    })

  })
})

// --------------------------------------------------
// 🔘 Filter toggles
// --------------------------------------------------

let filterToggles = document.querySelectorAll(".filter__toggle")
let artists = document.querySelectorAll(".artist")
let artistsWrap = document.querySelector(".artists")
let empty = document.querySelector('.empty')

function checkEmpty() {
  if(document.querySelectorAll('.artist:not(.artist--hidden, .artist--hidden-by-search)').length === 0) {
    gsap.to(empty, {
      opacity: 1
    })
    artistsWrap.classList.add('artists--empty')
  }
  else {
    gsap.to(empty, {
      opacity: 0
    })
    artistsWrap.classList.remove('artists--empty')
  }
}

function filter() {

  let filter = []

  filterToggles.forEach(filterToggle => {
    if(filterToggle.classList.contains('toggle--active')) {
      filter.push(filterToggle.dataset.category)
    }
  })

  // Artists
  let startHeight = gsap.getProperty(artistsWrap, 'height')
  let state = Flip.getState(artists)
  artists.forEach(artist => {

    let artistCategory = artist.dataset.category;

    let includesCategory = false;

    if(artistCategory.includes('; ')) {
      let artistCategories = artist.dataset.category.split('; ')
      artistCategories.forEach(artistCategory => {
        if(filter.includes(artistCategory)) {
          includesCategory = true;
        }
      })
    }
    else {
      includesCategory = filter.includes(artistCategory);
    }

    if(includesCategory) {
      artist.classList.remove('artist--hidden')
    } else {
      artist.classList.add('artist--hidden')
    }

  })
  let endHeight = gsap.getProperty(artistsWrap, 'height')
  gsap.set(artistsWrap, {
    height: endHeight
  })
  let flip = Flip.from(state, {
    duration: 0.5,
    ease: "power1.inOut",
    stagger: {
      amount: 0.25,
      grid: "auto"
    },
    absolute: true,
    simple: true,
    force3D: true,
    onEnter: elements => gsap.fromTo(elements, {opacity: 0, scale: 0}, {opacity: 1, scale: 1, ease: 'power1.inOut', duration: .5}),
    onLeave: elements => gsap.to(elements, {opacity: 0, scale: 0, ease: 'power1.inOut', duration: .5}),
  })
  let rect = artistsWrap.getBoundingClientRect()
  if(rect.top < 0) {
    gsap.to(window, {
      scrollTo: {
        y: artistsWrap,
        offsetY: 90
      },
      ease: "power1.inOut",
      duration: .5,
    })
  }
  flip.fromTo(artistsWrap, {
    height: startHeight
  },
    {
    height: endHeight,
    duration: flip.duration(),
    ease: 'power1.inOut',
    clearProps: 'height',
    onComplete: () => {
      setTimeout(() => {
        ScrollTrigger.refresh()
      }, 100)
    }
  }, 0)

  // Videos
  videos.forEach(video => {
    let videoCategories = video.dataset.category.split('; ')
    videoCategories.forEach(videoCategory => {
      if(filter.includes(videoCategory)) {
        video.classList.remove('video--hidden')
      } else {
        video.classList.add('video--hidden')
      }
    })
  })

  checkEmpty();

}

// --------------------------------------------------
// 🔘 Filter bar
// --------------------------------------------------

let filterBar = document.querySelector(".filter")
let filterBarButtons = document.querySelector(".filter__buttons")

if(filterBar) {

  gsap.to(filterBar, {
    autoAlpha: 0,
    scrollTrigger: {
      trigger: '.artists',
      invalidateOnRefresh: true,
      start: 'bottom top+=300',
      end: 'bottom top+=200',
      scrub: .25
    }
  })

  ScrollTrigger.create({
    trigger: filterBar,
    invalidateOnRefresh: false,
    start: -100,
    end: 'bottom bottom',
    onToggle: self => {
      if(self.isActive) {
        filterBar.classList.remove('filter--stuck')
      }
      else {
        filterBar.classList.add('filter--stuck')
      }
    }
  });

}

// --------------------------------------------------
// 🔎 Artist search
// --------------------------------------------------

let searchInput = document.querySelector('.search__input')
if(searchInput) {

  searchInput.addEventListener('input', debounce(() => {

    let startHeight = gsap.getProperty(artistsWrap, 'height')
    let state = Flip.getState(artists)

    let search = searchInput.value.toLowerCase()

    artists.forEach(artist => {
      let artistName = artist.querySelector('.artist__overlay').innerText.toLowerCase()
      let artistTags = artist.querySelector('.artist__tags').innerText.toLowerCase()
      let artistText = artistName + artistTags
      if(artistText.includes(search)) {
        artist.classList.remove('artist--hidden-by-search')
      }
      else {
        artist.classList.add('artist--hidden-by-search')
      }
    })

    let endHeight = gsap.getProperty(artistsWrap, 'height')
    gsap.set(artistsWrap, {
      height: endHeight
    })
    let rect = artistsWrap.getBoundingClientRect()
    if(rect.top < 0 || rect.top >= window.innerHeight) {
      gsap.to(window, {
        scrollTo: {
          y: artistsWrap,
          offsetY: 90
        },
        ease: "power1.inOut",
        duration: .5,
      })
    }
    let flip = Flip.from(state, {
      duration: 0.5,
      ease: "power1.inOut",
      stagger: {
        amount: 0.25,
        grid: "auto"
      },
      absolute: true,
      simple: true,
      force3D: false,
      onEnter: elements => gsap.fromTo(elements, {opacity: 0, scale: 0}, {opacity: 1, scale: 1, ease: 'power1.inOut', duration: .5}),
      onLeave: elements => gsap.to(elements, {opacity: 0, scale: 0, ease: 'power1.inOut', duration: .5}),
    })
    flip.fromTo(artistsWrap, {
      height: startHeight
    },
      {
      height: endHeight,
      duration: flip.duration(),
      ease: 'power1.inOut',
      clearProps: 'height',
      onComplete: () => {
        setTimeout(() => {
          ScrollTrigger.refresh()
        }, 100)
      }
    }, 0)

    checkEmpty();

  }, 250))

}

// --------------------------------------------------
// 🔠 Alphabet menu
// --------------------------------------------------

let scrollingToLetter = false
let alphabetLetters = document.querySelectorAll('.alphabet__letters--placeholder .alphabet__letter')
let alphabetLettersLeft = document.querySelectorAll('.alphabet__letters--left .alphabet__letter')
let alphabetLettersRight = document.querySelectorAll('.alphabet__letters--right .alphabet__letter')
let alphabet = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
let alphabetPosition = 0
let activeLetter = document.querySelector('.alphabet__active-letter')

function activateLetter(letter) {

  let actualLetter = letter.dataset.letter

  document.querySelector('.alphabet').dataset.activeLetter = actualLetter

  activeLetter.innerHTML = actualLetter

  alphabetLetters.forEach(otherLetter => {
    if(letter.dataset.letter == otherLetter.dataset.letter) {
      otherLetter.classList.add('alphabet__letter--active')
    }
    else {
      otherLetter.classList.remove('alphabet__letter--active')
    }
  })

  let activeLetterLeft = document.querySelector('.alphabet__letters--placeholder .alphabet__letter--active').offsetLeft

  gsap.to(activeLetter, {
    x: activeLetterLeft,
    duration: .25,
    ease: 'power1.inOut'
  })

  alphabetPosition = alphabet.indexOf(actualLetter)

  alphabetLettersLeft.forEach(letter => {
    if(alphabet.indexOf(letter.dataset.letter) >= alphabetPosition) {
      letter.classList.add('alphabet__letter--hidden')
    }
    else {
      letter.classList.remove('alphabet__letter--hidden')
    }
  })

  alphabetLettersRight.forEach(letter => {
    if(alphabet.indexOf(letter.dataset.letter) <= alphabetPosition) {
      letter.classList.add('alphabet__letter--hidden')
    }
    else {
      letter.classList.remove('alphabet__letter--hidden')
    }
  })

}

if(activeLetter) {
  activateLetter(alphabetLetters[0])
}

alphabetLetters.forEach(letter => {

  let letters = document.querySelectorAll('.alphabet__letter[data-letter="' + letter.dataset.letter + '"]');
  let artistsWithLetter = document.querySelectorAll('.artist[data-first-letter="' + letter.dataset.letter + '"]')

  if(artistsWithLetter.length) {

    letters.forEach(letter => {

      letter.addEventListener('click', () => {
        scrollingToLetter = true
        activateLetter(letter)
        gsap.to(window, {
          scrollTo: {
            y: artistsWithLetter[0],
            offsetY: 90
          },
          ease: "power1.inOut",
          onComplete: () => {
            scrollingToLetter = false
          },
          duration: .5,
        })
      })

    })

    ScrollTrigger.create({
      trigger: artistsWithLetter[0],
      start: 'top top+=90',
      end: 'bottom top+=90',
      invalidateOnRefresh: true,
      onEnter: () => {
        if(!scrollingToLetter) {
          activateLetter(letter)
        }
      },
    });

    ScrollTrigger.create({
      trigger: artistsWithLetter[artistsWithLetter.length - 1],
      start: 'top top+=90',
      end: 'bottom top+=90',
      invalidateOnRefresh: true,
      onEnterBack: () => {
        if(!scrollingToLetter) {
          activateLetter(letter)
        }
      },
    });

  }

  else {
    letters.forEach(letter => {
      letter.classList.add('alphabet__letter--none')
    })
  }

})

ScrollTrigger.create({
  trigger: artistsWrap,
  start: 'top top+=90',
  end: 'bottom top+=90',
  invalidateOnRefresh: true,
  onLeaveBack: () => {
    if(!scrollingToLetter) {
      activateLetter(alphabetLetters[0])
    }
  },
});

// --------------------------------------------------
// 🧑‍🎤 Artist hovers
// --------------------------------------------------

artists.forEach(artist => {

  let overlay = artist.querySelector('.artist__overlay')
  let image = artist.querySelector('.artist__image')

  let tl = gsap.timeline({
    paused: true,
    defaults: {
      duration: .25,
      ease: 'power1.inOut'
    }
  })
  .set(overlay, {
    display: 'flex'
  })
  .to(overlay, {
    autoAlpha: 1
  })
  if(image) {
    tl.to(image, {
      scale: 1.05,
    }, '<')
  }

  artist.addEventListener('mouseenter', () => {
    tl.play()
  })

  artist.addEventListener('mouseleave', () => {
    tl.reverse()
  })

})

// --------------------------------------------------
// 🌄 Overlays
// --------------------------------------------------

let currentlyOpenedOverlay = false;

function openOverlay(overlay, overlayLink) {

  currentlyOpenedOverlay = overlay;

  gsap.set(overlay, {
    autoAlpha: 0,
    yPercent: 100
  });

  overlay.classList.add('overlay--with-border')

  document.body.appendChild(overlay);

  let overlayImage = overlay.querySelectorAll('.artist-hero__image img')

  if(overlayImage) {
    imagesLoaded(overlayImage, () => {
      overlay.dispatchEvent(new Event('show'));
    })
  }
  else {
    overlay.dispatchEvent(new Event('show'));
  }

  let tl  = gsap.timeline({
    paused: true,
    defaults: {
      duration: .5,
      ease: 'power2.inOut',
    }
  })

  .to(overlay, {
    autoAlpha: 1,
    yPercent: 0,
  })

  .to(overlay, {
    borderRadius: 0,
    duration: .25,
  }, '-=.1')

  let tlPlayed = false;

  overlay.addEventListener('show', () => {
    if(!tlPlayed) {
      tl.play();
      tlPlayed = true;
      document.documentElement.classList.remove('loading');
      overlayLink.classList.remove('loading');
    }
  })

  let closeButton = overlay.querySelector('.overlay__close');
  closeButton.addEventListener('click', e => {
    closeOverlay(overlay);
    e.preventDefault();
  })

  setupBlocks(overlay);
  setupCases(overlay);
  setupExcerpt(overlay);
  setupCookieConsent(overlay);

}

function closeOverlay(overlay) {

  history.pushState(null, null, document.body.dataset.url);

  gsap.timeline({
    defaults: {
      duration: .5,
      ease: 'power2.inOut',
    },
    onComplete: function() {
      overlay.remove();
      currentlyOpenedOverlay = false;
    }
  })

  .to(overlay, {
    autoAlpha: 0,
    yPercent: 100,
    force3D: true,
  })

}

window.addEventListener("popstate", function (event) {
  if(currentlyOpenedOverlay) {
    closeOverlay(currentlyOpenedOverlay);
  }
});

document.addEventListener("keydown", function (event) {
  if (event.code == 'Escape') {
    if(currentlyOpenedOverlay) {
      closeOverlay(currentlyOpenedOverlay);
    }
  }
});

let overlayLinks = document.querySelectorAll('a[data-overlay]');

overlayLinks.forEach(overlayLink => {
  overlayLink.addEventListener('click', e => {

    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      return true;
    }

    if(!currentlyOpenedOverlay && !overlayLink.classList.contains('loading')) {
    
        let url = overlayLink.href
        overlayLink.classList.add('loading');
        document.documentElement.classList.add('loading');
    
        fetch(url).then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var content = doc.querySelector('.overlay');
          openOverlay(content, overlayLink);
          history.pushState(null, null, url);
        });

    }

    e.preventDefault()
  })
})

// --------------------------------------------------
// 🦶 Footer
// --------------------------------------------------

let footer = document.querySelector('.footer');
if(footer) {

  let about = footer.querySelector('.footer__about');

  ScrollTrigger.create({
    trigger: footer,
    start: 'top bottom',
    end: () => '+=' + about.offsetHeight,
    invalidateOnRefresh: true,
    onToggle: self => {
      if(self.isActive) {
        about.classList.add('footer__about--sticky');
      }
      else {
        about.classList.remove('footer__about--sticky');
      }
    }
  });
}

// --------------------------------------------------
// ⬜ Blocks
// --------------------------------------------------

const setupBlocks = (wrapper) => {

  let blocks = wrapper.querySelector('.blocks');

  if(blocks) {

    let moreButton = wrapper.querySelector('.blocks__more');

    if(moreButton) {
      
      moreButton.addEventListener('click', e => {
  
        let startHeight = gsap.getProperty(blocks, 'height');
  
        blocks.classList.add('blocks--no-limit');

        let endHeight = gsap.getProperty(blocks, 'height');

        gsap.fromTo(blocks, {
          height: startHeight
        }, {
          height: endHeight,
          duration: .5,
          ease: 'power1.inOut',
          clearProps: 'all',
        })

        e.preventDefault();
      })

    }
  }

}

setupBlocks(document);

// --------------------------------------------------
// 💼 Cases
// --------------------------------------------------

const autoHeight = (slider, activeSlide) => {

  if(activeSlide) {
    let activeSlideHeight = activeSlide.offsetHeight;
    gsap.to(slider.querySelector('.splide__track'), {
      height: activeSlideHeight,
      duration: .5,
      ease: 'power1.inOut',
    })
  }

};

const setupCases = (wrapper) => {

  let overlay = wrapper.querySelector('.overlay') || wrapper;
  let cases = wrapper.querySelector('.cases');
  let casesWrap = wrapper.querySelector('.cases__wrap.splide')

  if(cases && casesWrap) {
    
    let casesRect = cases.getBoundingClientRect();
    let next = cases.querySelector('.cases__next');
    let index = cases.querySelector('.index');
    let slides = cases.querySelectorAll('.splide__slide');

    casesWrap.splide = new Splide(casesWrap, {
      type: 'loop',
      pagination: false,
      arrows: false,
      clones: 2,
      autoHeight: true,
      updateOnMove: true,
      slideFocus: false,
    })

    casesWrap.splide.mount();

    next.addEventListener('click', e => {
      casesWrap.splide.go('>');
      e.preventDefault();
    })

    casesWrap.splide.on('active', e => {
      autoHeight(casesWrap, e.slide);
      index.innerHTML = casesWrap.splide.index + 1;
    })

    casesWrap.splide.on('resized', () => {
      autoHeight(casesWrap, casesWrap.querySelector('.splide__slide.is-visible'));
    })

    casesWrap.splide.on('move', () => {
      casesRect = cases.getBoundingClientRect();
    })

    casesWrap.splide.on('moved', () => {

      // if((casesRect.top < 0 && casesRect.top < 20) || (casesRect.top > 0 && casesRect.top > 50)) {
      //   gsap.to(casesWrap.closest('.overlay__inner'), {
      //     scrollTo: {
      //       y: casesWrap.closest('.cases')
      //     },
      //     ease: "power1.inOut",
      //     duration: .5,
      //   });
      // }

    })

    imagesLoaded(slides[0], () => {
      autoHeight(casesWrap, slides[0]);
    })

  }
  
  if(cases) {

    imagesLoaded(cases.querySelector('.splide__slide'), () => {
      gsap.to(wrapper.querySelector('.overlay__close'), {
        autoAlpha: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: cases,
          scroller: overlay.querySelector('.overlay__inner'),
          start: 'top top+=200',
          end:  'top top+=90',
          invalidateOnRefresh: true,
          scrub: .25,
        }
      })
    })

  }

}

setupCases(document);

const setupExcerpt = (wrapper) => {
  let artistIntro = wrapper.querySelector('.artist-intro__text');
  if(artistIntro) {

    let moreButton = artistIntro.querySelector('.artist-intro__more');

    if(artistIntro.querySelectorAll('p').length > 1) {
      
      artistIntro.classList.add('artist-intro__text--excerpt');
      moreButton.classList.add('artist-intro__more--active');

      moreButton.addEventListener('click', e => {

        let state = Flip.getState([artistIntro, artistIntro.querySelectorAll('p')]);

        artistIntro.classList.toggle('artist-intro__text--excerpt');

        Flip.from(state, {
          duration: .25,
          ease: "power1.inOut",
          simple: true,
          onEnter: elements => gsap.fromTo(elements, {opacity: 0}, {opacity: 1, ease: 'power1.inOut', duration: .25}),
          onLeave: elements => gsap.to(elements, {opacity: 0, ease: 'power1.inOut', duration: .25}),
        })

        e.preventDefault();
      })

    }


  }

}

setupExcerpt(document);

// --------------------------------------------------
// 🕺 Smooth scroll
// --------------------------------------------------

let anchors = document.querySelectorAll('a[href*="#"]');
anchors.forEach(anchor => {
  anchor.addEventListener('click', (e) => {
    let href = e.currentTarget.getAttribute('href');

    if(href != '#') {
      let target = document.querySelector(href);
      if(target) {
        gsap.to(window, {
          duration: 1,
          scrollTo: target,
          ease: "power2.inOut"
        });
        e.preventDefault();
      }
    }

  });
})


// --------------------------------------------------
// 🍪 Cookie Consent
// --------------------------------------------------

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

if(getCookie('consent') == 'true') {
  document.documentElement.classList.add('consented');
}

const setupCookieConsent = (wrapper) => {
  
  let cookieConsentButtons = wrapper.querySelectorAll('.gdpr-hint__button');
  cookieConsentButtons.forEach(cookieConsentButton => {
    cookieConsentButton.addEventListener('click', e => {
      document.documentElement.classList.add('consented');
      document.cookie = "consent=true";
      e.preventDefault();
    })
  })

}

setupCookieConsent(document);